import * as PropTypes from 'prop-types';
import * as React from 'react';
import { SanitizedRender } from '@reverbdotcom/discovery-ui';
import UrlHelpers from '../../../shared/url_helpers';
import DirectCheckoutWrapper from '../../../shop/direct_checkout_wrapper';
import DirectCheckoutProfile from './direct_checkout_profile';
import PaypalRadioButton from '../../../shop/paypal_radio_button';
import PaypalProfile from '../../../shop/paypal_profile';
import CurrencySelect from '../../../shop/currency_select';
import PaymentMethodSelectLoadingPlaceholder from '../../../shop/payment_method_select_loading_placeholder';
import ReactUJS from '../../../react_ujs';
import * as classNames from 'classnames';
import PaymentMethodUtils from './payment_method_utils';

const PAYPAL = 'paypal';
const DIRECT_CHECKOUT = 'direct_checkout';

function contactReverb() {
  return (
    <div className="scaling-mb-2">
      <div className="payment-setup">
        <div className="payment-setup__info">
          <div className="alert-box alert-box--blue">
            <SanitizedRender
              html={
                I18n.t('js.dashboard.selling.shopPolicies.contactReverb', {
                  linkOpen: '<a href="mailto:sellerengagement@reverb.com">',
                  linkClose: '</a>',
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

class PaymentMethodSelection extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showLegacyPaymentMethods: props.allowDirectCheckoutAndPaypal,
      selectedPaymentMethod: props.initiallySelectedPaymentMethod,
      legacyPaymentMethodsHtml: null,
      directCheckoutProfileAttributes: props.directCheckoutProfileProps.profileAttributes,
      canChangeShopCurrency: PaymentMethodUtils.canChangeShopCurrency(
        props.paymentSelection,
        props.initiallySelectedPaymentMethod,
      ),
    };
  }

  componentDidMount() {
    if (this.isLoadingLegacyForm) {
      $.get(UrlHelpers.shopPoliciesPaymentMethodsPath).done((htmlData) => {
        this.setState({
          legacyPaymentMethodsHtml: htmlData,
        });
      });
    }
  }

  // to render react components in the legacy form after AJAX load
  componentDidUpdate() {
    if (this.state.legacyPaymentMethodsHtml) {
      ReactUJS.init(window, this.wrapperRef);
    }
  }

  upgradeToDCHeading() {
    if (PaymentMethodUtils.shouldShowDCUpgradeHeading(this.props)) {
      return (
        <div className="dc-upgrade__ad">
          <p className="dc-upgrade__sub-heading">
            {I18n.t('js.dashboard.selling.shopPolicies.updateYourAccount')}
          </p>
          <h4 className="dc-upgrade__heading">
            {I18n.t('js.dashboard.selling.shopPolicies.switchToDirectCheckout')}
          </h4>
          <p className="dc-upgrade__paragraph">
            {I18n.t('js.dashboard.selling.shopPolicies.whyUpgrade')}
          </p>
        </div>
      );
    }
    return null;
  }

  directCheckoutProfileProps() {
    return Object.assign(
      {},
      this.props.directCheckoutProfileProps,
      {
        profileAttributes: this.state.directCheckoutProfileAttributes,
        selected: this.directCheckoutSelected(),
        shopCurrency: this.props.shopCurrency,
        onCountryChange: countryCode => this.updateDirectCheckoutProfileCountryCode(countryCode),
        onCurrencyChange: currency => this.updateSelectedPayoutCurrency(currency),
      },
    );
  }

  euDCUpgradePrompt() {
    return (
      <div className="dc-upgrade scaling-mb-2">
        {this.upgradeToDCHeading()}
        <DirectCheckoutWrapper
          directCheckoutFeePercent={this.props.directCheckoutFeePercent}
          directCheckoutIntlFeePercent={this.props.directCheckoutIntlFeePercent}
          shopCurrency={this.props.shopCurrency}
          bankCountryCode={this.state.directCheckoutProfileAttributes.countryCode}
          className="payment-setup__label--non-radio"
          paymentSelection={this.props.paymentSelection}
        />
        <div className="payment-setup">
          <div className="payment-setup__info">
            <a href={UrlHelpers.shopOnboardingSetupPath} className="btn btn-primary">
              {I18n.t('js.dashboard.selling.shopPolicies.getDirectCheckout')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  dcRadioButton() {
    return (
      <DirectCheckoutWrapper
        directCheckoutFeePercent={this.props.directCheckoutFeePercent}
        directCheckoutIntlFeePercent={this.props.directCheckoutIntlFeePercent}
        shopCurrency={this.props.shopCurrency}
        bankCountryCode={this.state.directCheckoutProfileAttributes.countryCode}
        paymentSelection={this.props.paymentSelection}
      >
        <input
          type="radio"
          value="direct_checkout"
          checked={this.directCheckoutSelected()}
          name={this.nameForField('payment_method')}
          onChange={event => this.handleChangePaymentMethod(event)}
          id="shop-settings-dc-radio"
        />
      </DirectCheckoutWrapper>
    );
  }

  internalDCForm() {
    return (
      <div
        className={
          classNames({
            'dc-upgrade scaling-mb-2': PaymentMethodUtils.shouldShowDCUpgradeHeading(this.props),
          })
        }
      >
        {this.upgradeToDCHeading()}
        {this.dcRadioButton()}
        <DirectCheckoutProfile {...this.directCheckoutProfileProps()} />
      </div>
    );
  }

  euDCRadioButton() {
    return (
      <div className="scaling-mb-2">
        {this.dcRadioButton()}
        <div className="payment-setup">
          <div className="payment-setup__info">
            {this.renderActions()}
          </div>
        </div>
      </div>
    );
  }

  directCheckoutOption() {
    const { paymentSelection } = this.props;

    if (paymentSelection === 'INTERNAL_DC_OR_PAYPAL') {
      return this.internalDCForm();
    }

    if (paymentSelection === 'EU_DC_OR_PAYPAL') {
      return this.euDCRadioButton();
    }

    if (paymentSelection === 'PAYPAL_WITH_EU_DC_UPGRADE') {
      return this.euDCUpgradePrompt();
    }

    if (paymentSelection === 'CONTACT_REVERB') {
      return contactReverb();
    }

    return null;
  }

  updateDirectCheckoutProfileCountryCode(countryCode) {
    const directCheckoutProfileAttributes = Object.assign(
      {},
      this.state.directCheckoutProfileAttributes,
      { countryCode },
    );

    this.setState({ directCheckoutProfileAttributes });
  }

  updateSelectedPayoutCurrency(currency) {
    const directCheckoutProfileAttributes = Object.assign(
      {},
      this.state.directCheckoutProfileAttributes,
      { currency },
    );

    this.setState({ directCheckoutProfileAttributes });
  }

  handleChangePaymentMethod(event) {
    const selectedPaymentMethod = event.target.value;
    this.setState({
      selectedPaymentMethod,
      canChangeShopCurrency: PaymentMethodUtils.canChangeShopCurrency(
        this.props.paymentSelection,
        selectedPaymentMethod,
      ),
    });
  }

  nameForField(fieldName) {
    return `${this.props.formPrefix}[${fieldName}]`;
  }

  paypalOption() {
    return (
      <div>
        <PaypalRadioButton
          inputName={this.nameForField('payment_method')}
          checked={this.paypalSelected()}
          handleChangePaymentMethod={event => this.handleChangePaymentMethod(event)}
          shopCurrency={this.props.shopCurrency}
        />
        <PaypalProfile
          selected={this.paypalSelected()}
          {...this.props.paypalProfileProps}
        />
      </div>
    );
  }

  paypalSelected() {
    return this.state.selectedPaymentMethod === PAYPAL;
  }

  directCheckoutSelected() {
    return this.state.selectedPaymentMethod === DIRECT_CHECKOUT;
  }

  supportedShopCurrencies() {
    if (this.directCheckoutSelected()) {
      return this.props.directCheckoutCurrencies;
    }
    return this.props.paypalCurrencies;
  }

  dontUpgradeButton() {
    if (this.props.allowDirectCheckoutAndPaypal) {
      const dontUpgradeButtonText = I18n.t('js.dashboard.legacyPaymentSelectForm.dontUpgradeHtml', {
        icon: '<span class="fa fa-arrow-circle-left"></span>',
      });

      return (
        <a
          data-legacy-toggle
          className="button button--small mt-1"
          onClick={() => this.toggleLegacyPaymentMethodForm()}
          dangerouslySetInnerHTML={{ __html: dontUpgradeButtonText }}
        />
      );
    }
    return null;
  }

  upgradingPaymentMethodField() {
    if (this.props.allowDirectCheckoutAndPaypal) {
      return (
        <input
          value="true"
          type="hidden"
          name={this.nameForField('upgrade_to_dco_or_paypal')}
        />
      );
    }
    return null;
  }

  paymentMethodElements() {
    if (!this.props.shopCurrency) {
      return null;
    }

    return (
      <div>
        {this.directCheckoutOption()}
        {this.paypalOption()}
        {this.upgradingPaymentMethodField()}
        {this.dontUpgradeButton()}
      </div>
    );
  }

  currencySelect() {
    if (!this.state.canChangeShopCurrency) {
      return null;
    }

    return (
      <CurrencySelect
        originalCurrencyCode={this.props.originalCurrencyCode}
        selectedPayoutCurrency={this.props.shopCurrency}
        currencies={this.supportedShopCurrencies()}
        currencyErrors={this.props.currencyErrors}
        onCurrencyChange={this.props.onCurrencyChange}
        directCheckoutSelected={this.directCheckoutSelected()}
      />
    );
  }

  directCheckoutOrPaypalSelect() {
    return (
      <div className="g-container shop-settings-payment-setup">
        {this.currencySelect()}
        {this.paymentMethodElements()}
      </div>
    );
  }

  toggleLegacyPaymentMethodForm() {
    this.setState({ showLegacyPaymentMethods: !this.state.showLegacyPaymentMethods });
  }

  directCheckoutAndPaypalSelect() {
    if (this.state.legacyPaymentMethodsHtml) {
      return (
        <div className="legacy-dc-and-paypal-form">
          <div className="module bd-1 bd--offwhite bd-rounded mb-2">
            <div className="align-center">
              <p className="strong small muted mb-0">
                {I18n.t('js.dashboard.legacyPaymentSelectForm.newFeature')}
              </p>
              <h4 className="heading-4 mb-half">
                {I18n.t('js.dashboard.legacyPaymentSelectForm.updatedPaymentMethods')}
              </h4>
              <p className="small mb-1">
                {I18n.t('js.dashboard.legacyPaymentSelectForm.upgradeExplainer')}
              </p>
              <a
                data-legacy-toggle
                className="button button--small button--orange"
                onClick={() => this.toggleLegacyPaymentMethodForm()}
              >
                {I18n.t('js.dashboard.legacyPaymentSelectForm.viewUpgradeOptions')}
              </a>
            </div>
          </div>
          <div dangerouslySetInnerHTML={{ __html: this.state.legacyPaymentMethodsHtml }} />
        </div>
      );
    }
    return null;
  }

  returnPaymentSelectForm() {
    if (this.state.showLegacyPaymentMethods) {
      return this.directCheckoutAndPaypalSelect();
    }
    return this.directCheckoutOrPaypalSelect();
  }

  isLoadingLegacyForm() {
    // when loading the legacy payment form html
    return (this.state.showLegacyPaymentMethods && !this.state.legacyPaymentMethodsHtml);
  }

  renderActions() {
    if (this.props.directCheckoutProfileProps.hasAdyenBankAccount) {
      return (
        <a href={UrlHelpers.editBankAccountPath} className="button">
          {I18n.t('js.dashboard.selling.shopPolicies.editBankAccount')}
        </a>
      );
    }

    return (
      <div className="alert alert-info">
        <p className="heading-4">
          {I18n.t('js.dashboard.selling.shopPolicies.completeSetupCta.title')}
        </p>
        <p>
          {I18n.t('js.dashboard.selling.shopPolicies.completeSetupCta.completeDetails')}
        </p>
        <a href={UrlHelpers.shopOnboardingSetupPath} className="button button--blue mt-2">
          {I18n.t('js.dashboard.selling.shopPolicies.completeSetupCta.buttonText')}
        </a>
      </div>
    );
  }

  render() {
    if (this.isLoadingLegacyForm()) {
      return <PaymentMethodSelectLoadingPlaceholder />;
    }

    return (
      <div ref={(el) => { this.wrapperRef = el; }}>
        {this.returnPaymentSelectForm()}
      </div>
    );
  }
}

PaymentMethodSelection.propTypes = {
  allowDirectCheckout: PropTypes.bool.isRequired,
  allowDirectCheckoutAndPaypal: PropTypes.bool.isRequired,
  directCheckoutFeePercent: PropTypes.number.isRequired,
  directCheckoutIntlFeePercent: PropTypes.number.isRequired,
  directCheckoutProfileProps: PropTypes.shape({
    profileAttributes: PropTypes.shape({
      nameOnAccount: PropTypes.string,
      maskedNameSummary: PropTypes.string,
      bankName: PropTypes.string,
      countryCode: PropTypes.string,
      payoutMethod: PropTypes.string,
      hasAcceptedReverbBankTerms: PropTypes.bool.isRequired,
      profileErrors: PropTypes.string,
    }).isRequired,
    payoutCurrencies: PropTypes.object.isRequired,
    initiallyReadOnly: PropTypes.bool,
    shopLocatedInUs: PropTypes.bool.isRequired,
    dcProfileComplete: PropTypes.bool.isRequired,
    bankLookupUrl: PropTypes.string.isRequired,
    preferredSeller: PropTypes.bool,
    hasCompletedPayouts: PropTypes.bool,
    hasAdyenBankAccount: PropTypes.bool,
    countryCodeOptions: PropTypes.array.isRequired,
    formPrefix: PropTypes.string.isRequired,
    reverbBankCurrencies: PropTypes.array.isRequired,
    selected: PropTypes.bool,
    shopCurrency: PropTypes.string,
  }).isRequired,
  paypalProfileProps: PropTypes.shape({
    initiallyReadOnly: PropTypes.bool.isRequired,
  }).isRequired,
  shopCurrency: PropTypes.string.isRequired,
  paypalCurrencies: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    code: PropTypes.string,
  })).isRequired,
  directCheckoutCurrencies: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    code: PropTypes.string,
  })).isRequired,
  currencyErrors: PropTypes.arrayOf(PropTypes.string),
  initiallySelectedPaymentMethod: PropTypes.string.isRequired,
  formPrefix: PropTypes.string.isRequired,
  paymentSelection: PropTypes.string.isRequired,
  originalCurrencyCode: PropTypes.string.isRequired,
  onCurrencyChange: PropTypes.func.isRequired,
};

PaymentMethodSelection.defaultProps = {
  currencyErrors: [],
};

export default PaymentMethodSelection;



// WEBPACK FOOTER //
// ./javascripts/components/dashboard/selling/shop_policies/payment_method_selection.jsx